import { useEffect, useMemo } from 'react';
import { debounce } from 'es-toolkit';

import { logger } from '@zep/core-logger';

import {
  type GetAllQuizesPayload,
  usePublicQuizListPayload,
} from '@/module/public/hooks/useGetAllQuizesPayload';
import { useLibraryContentTypeQuery } from '@/module/public/hooks/useLibraryContentTypeQuery';
import { useSearchLibrary } from '@/shared/features/quiz-library';

const logEvent = ({
  categoryFilters,
  subjectFilter,
  totalQuizCount,
  order,
  period,
}: Omit<GetAllQuizesPayload, 'enabled'> & { totalQuizCount?: number }) => {
  logger.track('filter_search', {
    ...categoryFilters,
    ...subjectFilter,
    templateType: categoryFilters.templateType ?? 'all',
    ...(totalQuizCount != null && { totalQuizCount }),
    order,
    period,
  });
};

const sharedDebouncedLogger = debounce(logEvent, 1000);

/**
 * 자료실 목록. 퀴즈와 퀴즈 폴더가 하나의 커서로 섞여 내려온다
 * (`POST /quiz-library/search`).
 *
 * 필터 페이로드는 퀴즈 전용 검색과 요청 타입이 같아 그대로 쓴다.
 */
export const usePublicLibraryList = () => {
  const { enabled, categoryFilters, subjectFilter, order, period } =
    usePublicQuizListPayload();
  const { contentType } = useLibraryContentTypeQuery();

  const result = useSearchLibrary({
    enabled,
    variables: {
      contentType,
      categoryFilters: {
        criteria: categoryFilters,
        ...subjectFilter,
      },
      order,
      period,
    },
  });

  const flattenedData = useMemo(
    () => result.data?.pages.flatMap(page => page.items) ?? [],
    [result.data],
  );

  useEffect(() => {
    if (result.data?.pages.length === 1) {
      const firstPage = result.data.pages[0];
      sharedDebouncedLogger({
        categoryFilters,
        subjectFilter,
        order,
        period,
        // 이름은 유지하되 의미가 바뀌었다 — 자료실이 혼합 검색으로 옮겨간 뒤로 이 수치는
        // 퀴즈만이 아니라 폴더까지 포함한다. 기존 지표 연속성을 위해 prop 명은 그대로 둔다.
        totalQuizCount: firstPage.totalCount ?? undefined,
      });
    }
  }, [categoryFilters, subjectFilter, order, period, result.data?.pages]);

  return {
    ...result,
    isLoading: !enabled ? true : result.isLoading,
    flatData: flattenedData,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
  };
};
